import { RaceLap } from '../models/TelemetryData';
import { formatLocation } from './bookingUtils';

/** "2:21.384" for a lap, "42.118" under a minute. */
export function formatLapTime(ms: number): string {
  const safe = Math.max(0, Math.round(ms));
  const minutes = Math.floor(safe / 60000);
  const seconds = Math.floor((safe % 60000) / 1000);
  const millis = String(safe % 1000).padStart(3, '0');
  return minutes > 0
    ? `${minutes}:${String(seconds).padStart(2, '0')}.${millis}`
    : `${seconds}.${millis}`;
}

/** "−2.684" when faster (a real minus sign), "+0.518" when slower, "±0.000" when equal. */
export function formatSignedDelta(ms: number): string {
  if (ms === 0) return '±0.000';
  return `${ms < 0 ? '−' : '+'}${formatLapTime(Math.abs(ms))}`;
}

/** A lap that counts: marked valid and timed. Invalid laps aren't ranked or compared. */
export const isCountedLap = (lap: RaceLap): boolean => lap.valid && lap.time > 0;

/** The earliest valid lap of the session (spec D8's comparison). */
export function firstValidLap(laps: RaceLap[]): RaceLap | null {
  return [...laps].filter(isCountedLap).sort((a, b) => a.lap - b.lap)[0] ?? null;
}

/** The fastest valid lap of the session. */
export function bestValidLap(laps: RaceLap[]): RaceLap | null {
  return [...laps].filter(isCountedLap).sort((a, b) => a.time - b.time)[0] ?? null;
}

/** Customer copy for a venue id. The Kansas venue is "Leawood" everywhere customers read (D17). */
export function venueDisplayName(location: string | null | undefined): string {
  if (!location) return '';
  const key = location.replace(/[\s_-]/g, '').toLowerCase();
  if (key === 'kansascity' || key === 'kansas' || key === 'leawood') return 'Leawood';
  const spaced = formatLocation(location);
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** "Tue, Oct 14". The API's lap timestamps carry no offset and are UTC. */
export function formatLapDate(date: string | null | undefined): string {
  if (!date) return '';
  const hasOffset = /[zZ]|[+-]\d{2}:?\d{2}$/.test(date);
  const d = new Date(hasOffset ? date : `${date}Z`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * The address "Copy link" shares: this lap, without `?offer=`. The offer id is the private key to
 * one driver's upgrade offer (spec §13), so a shared lap must not carry it to someone else.
 */
export function shareableLapUrl(href: string): string {
  try {
    const url = new URL(href);
    url.searchParams.delete('offer');
    return url.toString();
  } catch {
    return href;
  }
}
